import { AuditLog } from "../../../api/auditApi";
import { DriverHistoryTab } from "../auditEntityRegistry";

export type TimelineSegmentKind = "assigned" | "unassigned";

export interface AssignmentTimelineSegment {
  id: string;
  kind: TimelineSegmentKind;
  entityType?: "Vehicle" | "Trailer" | "Employee";
  entityId?: number;
  startAt: string;
  endAt?: string;
  assignAuditLog?: AuditLog;
  unassignAuditLog?: AuditLog;
  primaryAuditLog: AuditLog;
}

const ASSIGN_ACTIONS: Record<DriverHistoryTab, string> = {
  vehicle: "VehicleAssigned",
  trailer: "TrailerAssigned",
  dispatcher: "DispatcherAssigned",
};

const UNASSIGN_ACTIONS: Record<DriverHistoryTab, string> = {
  vehicle: "VehicleUnassigned",
  trailer: "TrailerUnassigned",
  dispatcher: "DispatcherUnassigned",
};

const ENTITY_TYPES: Record<DriverHistoryTab, AssignmentTimelineSegment["entityType"]> = {
  vehicle: "Vehicle",
  trailer: "Trailer",
  dispatcher: "Employee",
};

const parseMetadata = (metadata: string | null): Record<string, unknown> => {
  if (!metadata) {
    return {};
  }

  try {
    const parsed = JSON.parse(metadata);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const readNumber = (value: unknown) => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

export const getAssignmentEntityId = (
  auditLog: AuditLog,
  tab: DriverHistoryTab
): number | undefined => {
  const metadata = parseMetadata(auditLog.metadata);

  if (tab === "vehicle") {
    return (
      auditLog.links.find((link) => link.entityType === "Vehicle")?.entityId ??
      readNumber(metadata.vehicleId)
    );
  }

  if (tab === "trailer") {
    return (
      auditLog.links.find((link) => link.entityType === "Trailer")?.entityId ??
      readNumber(metadata.trailerId)
    );
  }

  return (
    auditLog.links.find(
      (link) => link.entityType === "Employee" && link.role === "Dispatcher"
    )?.entityId ?? readNumber(metadata.dispatcherEmployeeId)
  );
};

export const isAssignmentAssignAction = (action: string, tab: DriverHistoryTab) =>
  action === ASSIGN_ACTIONS[tab];

export const isAssignmentUnassignAction = (action: string, tab: DriverHistoryTab) =>
  action === UNASSIGN_ACTIONS[tab];

export const buildAssignmentTimelineSegments = (
  auditLogs: AuditLog[],
  tab: DriverHistoryTab
): AssignmentTimelineSegment[] => {
  const relevantLogs = [...auditLogs]
    .filter(
      (auditLog) =>
        isAssignmentAssignAction(auditLog.action, tab) ||
        isAssignmentUnassignAction(auditLog.action, tab)
    )
    .sort((left, right) => {
      const byDate = new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
      return byDate || left.id - right.id;
    });

  if (!relevantLogs.length) {
    return [];
  }

  const segments: AssignmentTimelineSegment[] = [];
  let currentAssignment: {
    entityId: number;
    startAt: string;
    assignAuditLog: AuditLog;
  } | null = null;
  let currentGapStart: { at: string; auditLog: AuditLog } | null = null;

  const pushGapSegment = (startAt: string, endAt: string | undefined, auditLog: AuditLog) => {
    segments.push({
      id: `gap-${auditLog.id}-${startAt}`,
      kind: "unassigned",
      startAt,
      endAt,
      unassignAuditLog: auditLog,
      primaryAuditLog: auditLog,
    });
  };

  const pushAssignmentSegment = (
    entityId: number,
    startAt: string,
    endAt: string | undefined,
    assignAuditLog: AuditLog,
    unassignAuditLog?: AuditLog
  ) => {
    segments.push({
      id: `assigned-${assignAuditLog.id}-${entityId}`,
      kind: "assigned",
      entityType: ENTITY_TYPES[tab],
      entityId,
      startAt,
      endAt,
      assignAuditLog,
      unassignAuditLog,
      primaryAuditLog: assignAuditLog,
    });
  };

  const isImmediateReassignment = (unassignIndex: number) => {
    const nextLog = relevantLogs[unassignIndex + 1];
    if (!nextLog || !isAssignmentAssignAction(nextLog.action, tab)) {
      return false;
    }

    const unassignTime = new Date(relevantLogs[unassignIndex].createdAt).getTime();
    const assignTime = new Date(nextLog.createdAt).getTime();
    return Math.abs(assignTime - unassignTime) < 5000;
  };

  for (let i = 0; i < relevantLogs.length; i++) {
    const auditLog = relevantLogs[i];
    const entityId = getAssignmentEntityId(auditLog, tab);
    const isAssign = isAssignmentAssignAction(auditLog.action, tab);

    if (isAssign) {
      if (currentGapStart) {
        pushGapSegment(currentGapStart.at, auditLog.createdAt, currentGapStart.auditLog);
        currentGapStart = null;
      }

      if (currentAssignment) {
        if (currentAssignment.entityId !== entityId) {
          pushAssignmentSegment(
            currentAssignment.entityId,
            currentAssignment.startAt,
            auditLog.createdAt,
            currentAssignment.assignAuditLog
          );
          currentAssignment = null;
        } else {
          continue;
        }
      }

      if (entityId) {
        currentAssignment = {
          entityId,
          startAt: auditLog.createdAt,
          assignAuditLog: auditLog,
        };
      }

      continue;
    }

    if (currentAssignment) {
      pushAssignmentSegment(
        currentAssignment.entityId,
        currentAssignment.startAt,
        auditLog.createdAt,
        currentAssignment.assignAuditLog,
        auditLog
      );
      currentAssignment = null;
    } else if (entityId) {
      pushAssignmentSegment(
        entityId,
        auditLog.createdAt,
        auditLog.createdAt,
        auditLog,
        auditLog
      );
    }

    if (!isImmediateReassignment(i)) {
      currentGapStart = { at: auditLog.createdAt, auditLog };
    }
  }

  if (currentAssignment) {
    pushAssignmentSegment(
      currentAssignment.entityId,
      currentAssignment.startAt,
      undefined,
      currentAssignment.assignAuditLog
    );
  } else if (currentGapStart) {
    pushGapSegment(currentGapStart.at, undefined, currentGapStart.auditLog);
  }

  return segments;
};

export const formatTimelineDate = (value: string) =>
  new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

export const formatTimelineTime = (value: string) =>
  new Date(value).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

export const formatTimelineDateTimeLabel = (value: string) => {
  const date = new Date(value);
  const dateLabel = date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const timeLabel = date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${dateLabel}, ${timeLabel}`;
};

export const formatTimelineDateTime = (value: string) => new Date(value).toLocaleString();

export const getAuditActorLabel = (auditLog: AuditLog) =>
  auditLog.actorName ||
  auditLog.actorEmail ||
  (auditLog.actorUserId ? `User #${auditLog.actorUserId}` : "System");

export const getTransitionAuditLog = (
  currentSegment: AssignmentTimelineSegment,
  nextSegment: AssignmentTimelineSegment
) => nextSegment.assignAuditLog ?? nextSegment.unassignAuditLog ?? nextSegment.primaryAuditLog;
